/**
 * Channel Billing-Entities Sync Policy
 *
 * Keeps the channel-detail billing-entity catalog fresh by SUBSCRIBING to
 * BillingEntities (snapshot + incremental changes) instead of a one-shot query.
 *
 * Why: the channel detail page is the mount host for the sales-channel setup
 * wizard, which can inline-create a billing entity. The previous one-shot load
 * (in `channelDetailLoadPolicy`) captured the list once when the detail view
 * opened — typically BEFORE that entity existed — and never refreshed. The stale
 * empty catalog then (a) left the billing-entity dropdown empty, and (b) let an
 * edit/save wipe a correctly-set `billingEntityId` to null, because the editor
 * cannot display a value that is missing from its (empty) options. A live
 * subscription removes the staleness at the source.
 *
 * The catalog is account-wide (not per-channel), so this subscribes once on mount
 * and keeps a local item map, re-dispatching the full list on every change. This
 * reuses the existing `detail/billingEntities/set` command — no upsert/remove
 * commands are needed. Mirrors the canonical `billingEntitiesSyncPolicy` (account
 * domain) and `channelsSyncPolicy` change handling.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { ChannelsState } from '../state';
import { ChannelsCommand } from '../commands';

export const createChannelBillingEntitiesSyncPolicy = (
  backend: ClientLibraryType
): Policy<ChannelsState, ChannelsCommand> => ({
  name: 'channel-billing-entities-sync',

  init: ({ dispatch }) => {
    const entitiesById = new Map<string, any>();

    const push = () => {
      dispatch(
        {
          type: 'integrations/channels/detail/billingEntities/set',
          payload: { entities: [...entitiesById.values()]}
        },
        { source: 'api' }
      );
    };

    dispatch(
      { type: 'integrations/channels/detail/loading/setBillingEntities', payload: { isLoading: true }},
      { source: 'api' }
    );

    const subscription = backend.subscriptions.BillingEntities({}, ( change: any ) => {
      if ( change?.type === 'snapshot' ) {
        entitiesById.clear();
        for ( const item of change.data?.items ?? []) {
          if ( item?.id ) entitiesById.set( item.id, item );
        }
        push();
        dispatch(
          { type: 'integrations/channels/detail/loading/setBillingEntities', payload: { isLoading: false }},
          { source: 'api' }
        );
        return;
      }
      if ( change?.type === 'signal' ) return;
      if ( change?.type === 'delete' ) {
        const removedId = change.previous?.id;
        if ( removedId && entitiesById.delete( removedId )) push();
        return;
      }
      if ( change?.data?.id ) {
        entitiesById.set( change.data.id, change.data );
        push();
      }
    });

    return () => {
      subscription?.close?.();
    };
  }
});
